import { useRef, useState } from 'react'

// Image utility functions to handle fallbacks and placeholders

/**
 * Supabase storage base URL for category images
 */
export const SUPABASE_CATEGORY_IMAGES_URL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/publicimages/category_images/`

/**
 * Supabase storage base URL for product images
 */
export const SUPABASE_PRODUCT_IMAGES_URL = `${import.meta.env.VITE_SUPABASE_URL}storage/v1/object/public/product-images/`;
/**
 * Get the full Supabase URL for a product image
 * @param imagePath - The image path from the database (from image_paths array)
 * @returns Full URL to the image or null if no path provided
 */
export const getSupabaseProductImageUrl = (imagePath: string | null | undefined): string | null => {
  
  if (!imagePath || imagePath.trim() === '') {
    return null;
  }
  
  const generatedUrl = `${SUPABASE_PRODUCT_IMAGES_URL}${imagePath.trim()}`;
  return generatedUrl;
};

/**
 * Get the primary product image URL from image_paths array
 * @param imagePaths - Array of image paths from product data
 * @returns Full URL to the first image or fallback if no images
 */
export const getPrimaryProductImageUrl = (imagePaths: string[] | null | undefined): string => {

  // Handle various falsy cases
  if (!imagePaths) {
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  if (!Array.isArray(imagePaths)) {
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  if (imagePaths.length === 0) {
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  const primaryImagePath = imagePaths[0];
  
  if (!primaryImagePath || primaryImagePath.trim() === '') {
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  const imageUrl = getSupabaseProductImageUrl(primaryImagePath);
  
  const finalUrl = imageUrl || DEFAULT_PRODUCT_IMAGE;
  
  return finalUrl;
};

/**
 * Get the primary product image URL from a product object
 * Handles different possible data structures from various API responses
 * @param product - Product object that may contain image_paths
 * @returns Full URL to the first image or fallback if no images
 */
export const getProductImageUrl = (product: any): string => {
  
  if (!product) {
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  // Try different possible property names for image paths
  const imagePaths = product.image_paths || product.imagePaths || product.images || product.image_urls;
  
  if (imagePaths) {
    return getPrimaryProductImageUrl(imagePaths);
  }
  
  // Check for single image property
  const singleImage = product.image_path || product.imagePath || product.image || product.image_url;
  if (singleImage) {
    const imageUrl = getSupabaseProductImageUrl(singleImage);
    return imageUrl || DEFAULT_PRODUCT_IMAGE;
  }
  
  return DEFAULT_PRODUCT_IMAGE;
};

/**
 * Get all product image URLs from image_paths array
 * @param imagePaths - Array of image paths from product data
 * @returns Array of full URLs to all images
 */
export const getAllProductImageUrls = (imagePaths: string[] | null | undefined): string[] => {
  if (!imagePaths || !Array.isArray(imagePaths) || imagePaths.length === 0) {
    return [DEFAULT_PRODUCT_IMAGE];
  }
  
  return imagePaths
    .map(path => getSupabaseProductImageUrl(path))
    .filter(url => url !== null) as string[];
};

/**
 * Get the full Supabase URL for a category image/icon
 * @param iconPath - The icon path from the database
 * @returns Full URL to the image or null if no path provided
 */
export const getSupabaseCategoryImageUrl = (iconPath: string | null | undefined): string | null => {
  if (!iconPath?.trim()) return null;
  
  const trimmedPath = iconPath.trim();
  
  // If already a full URL
  if (trimmedPath.startsWith('http')) return trimmedPath;
  
  // Remove leading slash if present
  const cleanPath = trimmedPath.startsWith('/') 
    ? trimmedPath.slice(1) 
    : trimmedPath;
  
  return `${SUPABASE_CATEGORY_IMAGES_URL}${cleanPath}`;
};

/**
 * Creates a base64 encoded placeholder image SVG
 * @param width - Image width (default: 400)
 * @param height - Image height (default: 400)
 * @param text - Text to display in placeholder (default: "No Image")
 * @param bgColor - Background color hex (default: "#F3F4F6")
 * @param textColor - Text color hex (default: "#6B7280")
 * @returns Base64 encoded data URL
 */
export const createPlaceholderImage = (
  width: number = 400,
  height: number = 400,
  text: string = "No Image",
  bgColor: string = "#F3F4F6",
  textColor: string = "#6B7280"
): string => {
 
  
  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" fill="${bgColor}"/>
      <path d="M175 150H150V125H175V150ZM225 200H200V225H225V200ZM200 175H175V200H200V175ZM225 150H200V175H225V150ZM225 125H200V150H225V125ZM250 175H225V200H250V175ZM250 150H225V175H250V150Z" fill="#9CA4AF"/>
      <text x="${width/2}" y="${height/2 + 20}" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="16">${text}</text>
    </svg>
  `;
  
  const base64Svg = `data:image/svg+xml;base64,${btoa(svg)}`;
  
  
  return base64Svg;
};

/**
 * Default placeholder image for products
 */
export const DEFAULT_PRODUCT_IMAGE = createPlaceholderImage(400, 400, "No Image", "#F3F4F6", "#6B7280");

/**
 * Placeholder for category icons
 */
export const DEFAULT_CATEGORY_ICON = createPlaceholderImage(64, 64, "Icon", "#E5E7EB", "#9CA3AF");

/**
 * Handles image error and sets fallback
 * @param event - Image error event
 * @param fallbackUrl - Optional custom fallback URL
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
  fallbackUrl?: string
) => {
  const img = event.target as HTMLImageElement;
  
  
  
  // Prevent infinite loop
  if (img.dataset.fallbackAttempted === "true") {
    
    return;
  }
  
  img.dataset.fallbackAttempted = "true";
  const newSrc = fallbackUrl || DEFAULT_PRODUCT_IMAGE;
  
  img.src = newSrc;
};

/**
 * Validates if an image URL is accessible
 * @param url - Image URL to validate
 * @returns Promise<boolean>
 */
export const validateImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Get optimized image URL with fallback
 * @param imageUrl - Original image URL
 * @param options - Size and quality options
 * @returns Promise<string> - Optimized or fallback URL
 */
export const getOptimizedImageUrl = async (
  imageUrl: string | null | undefined,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    fallback?: string;
  } = {}
): Promise<string> => {
  if (!imageUrl) {
    return options.fallback || DEFAULT_PRODUCT_IMAGE;
  }

  // If it's already a data URL, return as is
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  // For assets, add proper path if needed
  if (imageUrl.startsWith('assets/') || imageUrl.startsWith('/assets/')) {
    const fullPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    
    // Validate the asset exists
    const isValid = await validateImageUrl(fullPath);
    return isValid ? fullPath : (options.fallback || DEFAULT_PRODUCT_IMAGE);
  }

  // For external URLs, validate first
  const isValid = await validateImageUrl(imageUrl);
  return isValid ? imageUrl : (options.fallback || DEFAULT_PRODUCT_IMAGE);
};

/**
 * Get image with immediate fallback (synchronous version)
 * @param imageUrl - Image URL
 * @param fallback - Custom fallback image
 * @returns Image URL with fallback
 */
export const getImageWithFallback = (
  imageUrl: string | null | undefined,
  fallback?: string
): string => {
  if (!imageUrl) {
    return fallback || DEFAULT_PRODUCT_IMAGE;
  }

  // If it's already a data URL, return as is
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  // For assets, ensure proper path
  if (imageUrl.startsWith('assets/') && !imageUrl.startsWith('/')) {
    return `/${imageUrl}`;
  }

  return imageUrl;
};

/**
 * Get the last product image URL from image_paths array
 * @param imagePaths - Array of image paths from product data
 * @returns Full URL to the last image or fallback if no images
 */
export const getLastProductImageUrl = (imagePaths: string[] | null | undefined): string => {
  if (!imagePaths || !Array.isArray(imagePaths) || imagePaths.length === 0) {
    return DEFAULT_PRODUCT_IMAGE;
  }
  const lastImagePath = imagePaths[imagePaths.length - 1];
  if (!lastImagePath || lastImagePath.trim() === '') {
    return DEFAULT_PRODUCT_IMAGE;
  }
  const imageUrl = getSupabaseProductImageUrl(lastImagePath);
  return imageUrl || DEFAULT_PRODUCT_IMAGE;
};


export const getLast=(imagePaths: string[]):string => {
return imagePaths[imagePaths.length -1] || DEFAULT_PRODUCT_IMAGE;
}

export  const useImageZoom = () => {
      const containerRef = useRef<HTMLDivElement>(null);
      const [isHovered, setIsHovered] = useState(false);
      const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

      const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
          const rect = containerRef.current?.getBoundingClientRect();
          if (!rect) return;

          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          
          setZoomPosition({ x, y });
      }
      return { containerRef, isHovered, setIsHovered, zoomPosition, handleMouseMove };
  }